/**
 * Parent-facing "Schedule" screen — the plan of care the DOCTOR prescribed.
 *
 * Three read-mostly sections:
 *  1. Surveillance — the doctor-prescribed cadence (NFS, fundus, ETF, BAER,
 *     weight/dose review, pediatric consult, …) computed from DOB + cadence,
 *     grouped by urgency (overdue / due now / upcoming / done). For a due or
 *     overdue occurrence that has no scheduled appointment yet, the parent can
 *     "Book" it (POST /appointments) with a date they pick (default today).
 *  2. Appointments — the visits on the calendar. The parent can report that
 *     they BOOKED or ATTENDED a visit (POST /appointments/:id/parent-confirm).
 *     This is a family report ONLY — it never marks the visit clinically
 *     completed; the doctor still reviews and records the result.
 *  3. Pending labs — orders flagged as redraw-needed or pending-too-long, so
 *     the family knows to follow up with the lab.
 *
 * The parent NEVER sees clinician findings and NEVER sets clinical status.
 * App-specific copy not in the shared catalog uses a tiny inline FR/EN map
 * keyed off the current locale (same style as components/ReportUpload.tsx).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card, Badge, Button, inputClass } from '../../components/ui.js';
import { ErrorState, QueryState } from '../../components/form.js';

// --- Inline bilingual strings (not present in the shared catalog) -----------
const L = {
  title: { fr: 'Calendrier de suivi', en: 'Care schedule' },
  subtitle: {
    fr: 'Ce que le médecin a demandé de faire.',
    en: 'What the doctor asked us to do.',
  },
  safetyNote: {
    fr: 'Vous pouvez confirmer un rendez-vous pris ou effectué. C’est un signalement de la famille : le médecin examine et enregistre lui-même les résultats.',
    en: 'You can confirm an appointment was booked or attended. This is a family report: the doctor reviews and records the results.',
  },
  survTitle: { fr: 'Examens de surveillance', en: 'Surveillance exams' },
  grpOverdue: { fr: 'En retard', en: 'Overdue' },
  grpDue: { fr: 'À faire maintenant', en: 'Due now' },
  grpUpcoming: { fr: 'À venir', en: 'Upcoming' },
  grpDone: { fr: 'Déjà faits', en: 'Already done' },
  showDone: { fr: 'Afficher les examens faits', en: 'Show completed exams' },
  hideDone: { fr: 'Masquer les examens faits', en: 'Hide completed exams' },
  bookAction: { fr: 'J’ai pris rendez-vous', en: 'Book / I booked it' },
  booking: { fr: 'Enregistrement…', en: 'Saving…' },
  bookDate: { fr: 'Date du rendez-vous', en: 'Appointment date' },
  apptTitle: { fr: 'Rendez-vous', en: 'Appointments' },
  markBooked: { fr: 'Marquer comme réservé', en: 'Mark booked' },
  markAttended: { fr: 'Marquer comme effectué', en: 'Mark attended' },
  saving: { fr: 'Enregistrement…', en: 'Saving…' },
  attendedNote: {
    fr: 'Merci. Le médecin examinera et enregistrera le résultat.',
    en: 'Thank you. The doctor will review and record the result.',
  },
  noSpecialist: { fr: 'Spécialiste non précisé', en: 'No specialist listed' },
  yourReport: { fr: 'Votre signalement', en: 'Your report' },
  labsTitle: { fr: 'Analyses en attente', en: 'Pending labs' },
  redrawNeeded: { fr: 'Nouveau prélèvement nécessaire', en: 'Redraw needed' },
  pendingTooLong: { fr: 'En attente depuis trop longtemps', en: 'Pending too long' },
  daysPending: { fr: 'jours en attente', en: 'days pending' },
  none: { fr: 'Rien pour le moment.', en: 'Nothing for now.' },
  noneLabs: { fr: 'Aucune analyse à relancer.', en: 'No labs to follow up.' },
} as const;

type Lang = 'fr' | 'en';
type SurvStatus = 'upcoming' | 'due' | 'overdue' | 'done';
type ParentConfirmation = 'booked' | 'attended' | 'missed';

interface Occurrence {
  examType: string;
  dueDate: string;
  status: SurvStatus;
  matchedCompletedId?: string;
  phaseLabel?: string;
}

interface Appointment {
  id: string;
  date: string;
  examType: string;
  specialist: string | null;
  reason: string | null;
  status: string;
  nextDate: string | null;
  parentConfirmation: ParentConfirmation | null;
  parentConfirmedAt: string | null;
}

interface LabOrder {
  id: string;
  kind: string;
  sampleDate: string;
  status: string;
  resultDate: string | null;
  flags: { pendingTooLong: boolean; redrawNeeded: boolean; daysPending: number | null };
}

const survTone: Record<SurvStatus, 'info' | 'warn' | 'danger' | 'ok'> = {
  overdue: 'danger',
  due: 'warn',
  upcoming: 'info',
  done: 'ok',
};

const confirmTone: Record<ParentConfirmation, 'info' | 'warn' | 'danger' | 'ok'> = {
  booked: 'info',
  attended: 'ok',
  missed: 'warn',
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function apptStatusTone(status: string): 'info' | 'warn' | 'danger' | 'ok' {
  if (status === 'completed') return 'ok';
  if (status === 'missed') return 'danger';
  if (status === 'cancelled' || status === 'rescheduled') return 'warn';
  return 'info'; // scheduled and anything else
}

export function ParentSchedule() {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const lang: Lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  const [showDone, setShowDone] = useState(false);
  // Per-occurrence chosen booking date (defaults to today). Keyed by examType+dueDate.
  const [bookDates, setBookDates] = useState<Record<string, string>>({});

  const surveillance = useQuery({
    queryKey: ['surveillance'],
    queryFn: () => api.get<{ occurrences: Occurrence[] }>('/surveillance'),
  });
  const appointments = useQuery({
    queryKey: ['appointments'],
    queryFn: () => api.get<{ appointments: Appointment[] }>('/appointments'),
  });
  const labs = useQuery({
    queryKey: ['labs', 'orders'],
    queryFn: () => api.get<{ orders: LabOrder[] }>('/labs/orders'),
  });

  function invalidateAll() {
    void qc.invalidateQueries({ queryKey: ['surveillance'] });
    void qc.invalidateQueries({ queryKey: ['appointments'] });
    void qc.invalidateQueries({ queryKey: ['labs', 'orders'] });
  }

  // "Book this exam" — caregiver POST forces status=scheduled server-side.
  const bookM = useMutation({
    mutationFn: ({ date, examType }: { date: string; examType: string }) =>
      api.post('/appointments', { date, examType }),
    onSuccess: invalidateAll,
  });

  // "Mark booked / attended" — family report only; never clinically completes.
  const confirmM = useMutation({
    mutationFn: ({ id, confirmation }: { id: string; confirmation: ParentConfirmation }) =>
      api.post(`/appointments/${id}/parent-confirm`, { confirmation }),
    onSuccess: invalidateAll,
  });

  const occurrences = surveillance.data?.occurrences ?? [];
  const overdue = occurrences.filter((o) => o.status === 'overdue');
  const due = occurrences.filter((o) => o.status === 'due');
  const upcoming = occurrences.filter((o) => o.status === 'upcoming');
  const done = occurrences.filter((o) => o.status === 'done');

  // examTypes that already have a scheduled appointment (don't offer "Book" again).
  const scheduledExamTypes = new Set(
    (appointments.data?.appointments ?? [])
      .filter((a) => a.status !== 'completed' && a.parentConfirmation !== 'missed')
      .map((a) => a.examType),
  );

  const sortedAppointments = [...(appointments.data?.appointments ?? [])].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0,
  );

  const labOrders = (labs.data?.orders ?? []).filter(
    (o) => o.flags.redrawNeeded || o.flags.pendingTooLong,
  );

  function renderOccurrence(o: Occurrence, idx: number) {
    const key = `${o.examType}-${o.dueDate}-${idx}`;
    const canBook =
      (o.status === 'due' || o.status === 'overdue') && !scheduledExamTypes.has(o.examType);
    const bookDate = bookDates[key] ?? todayISO();
    const isBooking = bookM.isPending && bookM.variables?.examType === o.examType && bookM.variables?.date === bookDate;
    return (
      <li key={key} className="rounded-lg border border-slate-200 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-800">{t(`exam.${o.examType}`)}</div>
            <div className="text-xs text-slate-500">
              {o.dueDate}
              {o.phaseLabel ? ` — ${o.phaseLabel}` : ''}
            </div>
          </div>
          <Badge tone={survTone[o.status]}>{t(`surv.${o.status}`)}</Badge>
        </div>
        {canBook && (
          <div className="mt-2 border-t border-slate-100 pt-2">
            <p className="text-xs text-slate-500 mb-2">{t('action.bookExam')}</p>
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-slate-600">
                <span className="block mb-1">{tr(L.bookDate)}</span>
                <input
                  type="date"
                  className={`${inputClass} w-auto`}
                  value={bookDate}
                  onChange={(e) => setBookDates((d) => ({ ...d, [key]: e.target.value }))}
                />
              </label>
              <Button
                type="button"
                disabled={bookM.isPending}
                onClick={() => bookM.mutate({ date: bookDate, examType: o.examType })}
              >
                {isBooking ? tr(L.booking) : tr(L.bookAction)}
              </Button>
            </div>
          </div>
        )}
      </li>
    );
  }

  function survGroup(label: string, items: Occurrence[]) {
    if (items.length === 0) return null;
    return (
      <div className="mb-4 last:mb-0">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">
          {label} <span className="text-slate-400 font-normal">({items.length})</span>
        </h3>
        <ul className="space-y-2">{items.map((o, i) => renderOccurrence(o, i))}</ul>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-800">{tr(L.title)}</h1>
        <p className="text-sm text-slate-500">{tr(L.subtitle)}</p>
      </div>

      {/* Surveillance — doctor-prescribed cadence, grouped by urgency. */}
      <Card title={tr(L.survTitle)}>
        <QueryState
          isLoading={surveillance.isLoading}
          isError={surveillance.isError}
          error={surveillance.error}
          errorFallback={t('common.error')}
          isEmpty={occurrences.length === 0}
          emptyMessage={tr(L.none)}
        >
          {bookM.isError && (
            <div className="mb-3">
              <ErrorState error={bookM.error} fallback={t('common.error')} />
            </div>
          )}
          {survGroup(tr(L.grpOverdue), overdue)}
          {survGroup(tr(L.grpDue), due)}
          {survGroup(tr(L.grpUpcoming), upcoming)}
          {done.length > 0 && (
            <div className="border-t border-slate-100 pt-3">
              <button
                type="button"
                className="text-xs underline text-brand"
                onClick={() => setShowDone((s) => !s)}
              >
                {showDone ? tr(L.hideDone) : `${tr(L.showDone)} (${done.length})`}
              </button>
              {showDone && <div className="mt-3">{survGroup(tr(L.grpDone), done)}</div>}
            </div>
          )}
        </QueryState>
      </Card>

      {/* Appointments — family can report booked/attended (never completes). */}
      <Card title={tr(L.apptTitle)}>
        <p className="text-xs text-slate-400 mb-3">{tr(L.safetyNote)}</p>
        <QueryState
          isLoading={appointments.isLoading}
          isError={appointments.isError}
          error={appointments.error}
          errorFallback={t('common.error')}
          isEmpty={sortedAppointments.length === 0}
          emptyMessage={tr(L.none)}
        >
          {confirmM.isError && (
            <div className="mb-3">
              <ErrorState error={confirmM.error} fallback={t('common.error')} />
            </div>
          )}
          <ul className="space-y-2">
            {sortedAppointments.map((a) => {
              const attended = a.parentConfirmation === 'attended';
              const busy = confirmM.isPending && confirmM.variables?.id === a.id;
              return (
                <li key={a.id} className="rounded-lg border border-slate-200 px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-800">
                        {t(`exam.${a.examType}`)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {a.date} — {a.specialist?.trim() || tr(L.noSpecialist)}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge tone={apptStatusTone(a.status)}>{t(`appt.status.${a.status}`)}</Badge>
                      {a.parentConfirmation && (
                        <span className="flex items-center gap-1 text-[11px] text-slate-400">
                          {tr(L.yourReport)}:
                          <Badge tone={confirmTone[a.parentConfirmation]}>
                            {t(`apptConfirm.${a.parentConfirmation}`)}
                          </Badge>
                        </span>
                      )}
                    </div>
                  </div>
                  {!attended && (
                    <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-100 pt-2">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={confirmM.isPending}
                        onClick={() => confirmM.mutate({ id: a.id, confirmation: 'booked' })}
                      >
                        {busy && confirmM.variables?.confirmation === 'booked' ? tr(L.saving) : tr(L.markBooked)}
                      </Button>
                      <Button
                        type="button"
                        disabled={confirmM.isPending}
                        onClick={() => confirmM.mutate({ id: a.id, confirmation: 'attended' })}
                      >
                        {busy && confirmM.variables?.confirmation === 'attended' ? tr(L.saving) : tr(L.markAttended)}
                      </Button>
                    </div>
                  )}
                  {attended && (
                    <p className="mt-2 border-t border-slate-100 pt-2 text-xs text-slate-500">
                      {tr(L.attendedNote)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </QueryState>
      </Card>

      {/* Pending labs — redraw / pending-too-long flags to follow up. */}
      <Card title={tr(L.labsTitle)}>
        <QueryState
          isLoading={labs.isLoading}
          isError={labs.isError}
          error={labs.error}
          errorFallback={t('common.error')}
          isEmpty={labOrders.length === 0}
          emptyMessage={tr(L.noneLabs)}
        >
          <ul className="space-y-2">
            {labOrders.map((o) => (
              <li key={o.id} className="rounded-lg border border-slate-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">{o.kind}</div>
                    <div className="text-xs text-slate-500">{o.sampleDate}</div>
                  </div>
                  <Badge tone={o.flags.redrawNeeded ? 'danger' : 'warn'}>
                    {o.flags.redrawNeeded ? tr(L.redrawNeeded) : tr(L.pendingTooLong)}
                  </Badge>
                </div>
                <p className="mt-2 border-t border-slate-100 pt-2 text-xs text-slate-500">
                  {t('action.followUpLab')}
                  {o.flags.daysPending != null ? ` (${o.flags.daysPending} ${tr(L.daysPending)})` : ''}
                </p>
              </li>
            ))}
          </ul>
        </QueryState>
      </Card>
    </div>
  );
}
